import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Header } from "@/components/Header";
import { useT, useLocale } from "@/i18n/locale-context";
import { useJurisdiction } from "@/hooks/useJurisdiction";
import { supabase } from "@/integrations/supabase/client";
import { getProtocol } from "@/lib/protocols/definitions";
import { observeProtocolStep } from "@/lib/protocol-observe.functions";
import { startProtocol, getLastStepImage } from "@/lib/protocols.functions";
import { prepareImage } from "@/lib/image-prepare";
import { getStoredConsent } from "@/components/GdprConsentModal";
import { CaptureCoach, type CaptureQuality } from "@/components/capture/CaptureCoach";
import { CaptureProPanel } from "@/components/capture/CaptureProPanel";
import { usePremium } from "@/hooks/usePremium";
import { PhotoGuide } from "@/components/protocols/PhotoGuide";
import { getPhotoGuideProfile, pickL10n } from "@/components/protocols/photo-guide-profiles";

export const Route = createFileRoute("/protocols/$goal_/capture/$stepKey")({
  head: () => ({ meta: [{ title: "Capture guidée — Glow Up Coach" }] }),
  component: CapturePage,
});

type Observation = {
  summary?: string;
  visibleFeatures?: Array<{ label: string; intensity?: string; note?: string }>;
  measurements?: Array<{ label: string; estimateMm?: number; uncertaintyPct?: number }>;
  changesVsPrevious?: string | null;
  redFlags?: string[];
  nextActions?: string[];
  requiresPractitionerReview?: boolean;
  practitionerReviewReason?: string | null;
  imageQuality?: { lighting?: string; focus?: string; framing?: string };
  disclaimer?: string;
};

function CapturePage() {
  const { goal, stepKey } = Route.useParams();
  const def = getProtocol(goal);
  const step = def?.steps.find((s) => s.key === stepKey);
  const t = useT();
  const { locale } = useLocale();
  const { country, jurisdiction } = useJurisdiction();
  const nav = useNavigate();
  const { isPro } = usePremium();

  const observe = useServerFn(observeProtocolStep);
  const start = useServerFn(startProtocol);
  const fetchLastImage = useServerFn(getLastStepImage);

  const guideProfile = getPhotoGuideProfile(goal);
  const viewsExpected = Math.max(1, guideProfile.views ?? 1);
  const viewLabels: string[] = (guideProfile.viewLabels ?? []).map((l) => pickL10n(l, locale));

  const [sessionId, setSessionId] = useState<string | null>(null);
  const [authed, setAuthed] = useState(false);
  const [preview, setPreview] = useState<string | null>(null);
  const [pendingDataUrl, setPendingDataUrl] = useState<string | null>(null);
  /** When the protocol expects multiple angles (e.g. posture front/back/side),
   *  every uploaded image is queued here in order. Index 0 is sent to the AI
   *  for primary analysis; the rest are stored as additional step events so
   *  the user can review them later. */
  const [extraViews, setExtraViews] = useState<string[]>([]);
  const [skipRemaining, setSkipRemaining] = useState(false);
  const [referenceObject, setReferenceObject] = useState("");
  const [pain, setPain] = useState<number>(0);
  const [redness, setRedness] = useState<number>(0);
  const [itch, setItch] = useState<number>(0);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<Observation | null>(null);
  const [previousUrl, setPreviousUrl] = useState<string | null>(null);
  const [captureQuality, setCaptureQuality] = useState<CaptureQuality | null>(null);

  useEffect(() => {
    supabase.auth.getSession().then(async ({ data }) => {
      if (!data.session) { nav({ to: "/login" }); return; }
      setAuthed(true);
      // Ensure a protocol session exists for this goal.
      try {
        const r = await start({ data: { goal, country: country ?? undefined, jurisdiction } });
        setSessionId(r.sessionId);
      } catch (e) {
        console.error(e);
      }
      // Fetch ghost image for re-framing (if any prior capture exists).
      try {
        const g = await fetchLastImage({ data: { goal, stepKey } });
        if (g.url) setPreviousUrl(g.url);
      } catch { /* non-blocking */ }
    });
  }, [nav, start, fetchLastImage, goal, stepKey, country, jurisdiction]);

  if (!def || !step) {
    return (
      <div className="min-h-screen bg-background text-foreground p-12 text-center">
        <p>{t("protocols.not_found")}</p>
        <Link to="/protocols" className="underline mt-4 inline-block">{t("protocols.back")}</Link>
      </div>
    );
  }

  const bodyArea = step.bodyArea ?? "face";

  async function onFile(file: File) {
    if (file.size > 45 * 1024 * 1024) { toast.error("Image > 45 MB"); return; }
    const prepared = await prepareImage(file);
    if (!pendingDataUrl) {
      setPendingDataUrl(prepared.dataUrl);
      setPreview(prepared.dataUrl);
    } else {
      // Already have the primary view — queue this as an extra angle.
      setExtraViews((v) => (v.length + 1 >= viewsExpected ? v : [...v, prepared.dataUrl]));
      setPreview(prepared.dataUrl);
    }
  }

  /** Reset every captured view and start fresh from this new file. */
  async function onRetake(file: File) {
    if (file.size > 45 * 1024 * 1024) { toast.error("Image > 45 MB"); return; }
    const prepared = await prepareImage(file);
    setPendingDataUrl(prepared.dataUrl);
    setPreview(prepared.dataUrl);
    setExtraViews([]);
    setSkipRemaining(false);
  }

  const totalCaptured = (pendingDataUrl ? 1 : 0) + extraViews.length;
  const needsMoreViews = viewsExpected > 1 && totalCaptured < viewsExpected;
  const nextViewLabel = needsMoreViews ? viewLabels[totalCaptured] ?? `${totalCaptured + 1} / ${viewsExpected}` : null;

  async function onSubmit() {
    if (!sessionId || !pendingDataUrl) return;
    // Multi-view confirmation: if user hasn't captured every recommended view
    // and hasn't explicitly acknowledged the reduced precision, ask once.
    if (needsMoreViews && !skipRemaining) {
      toast.warning(t("protocols.capture.views.missing.toast"), {
        description: t("protocols.capture.views.missing.desc"),
        action: {
          label: t("protocols.capture.views.skip"),
          onClick: () => setSkipRemaining(true),
        },
      });
      return;
    }
    setBusy(true);
    try {
      const consent = getStoredConsent();
      const storePhoto = !!consent?.storePhotos;
      const viewsNote = viewsExpected > 1
        ? `[Vues: ${totalCaptured}/${viewsExpected}${viewLabels.length ? ` — ${viewLabels.slice(0, totalCaptured).join(" + ")}` : ""}${totalCaptured < viewsExpected ? " — précision réduite, vues manquantes" : ""}]`
        : "";
      const r = await observe({
        data: {
          sessionId,
          stepKey,
          // Server validates via z.enum(PROTOCOL_GOALS); pass through as-is.
          goal: goal as never,
          bodyArea,
          imageBase64: pendingDataUrl,
          referenceObject: referenceObject || undefined,
          storePhoto,
          locale,
          jurisdiction,
          captureQuality: captureQuality ?? undefined,
          userObservations: {
            pain: pain || undefined,
            redness: redness || undefined,
            itch: itch || undefined,
            notes: [viewsNote, notes].filter(Boolean).join("\n") || undefined,
          },
        },
      });
      setResult(r.observation as Observation);
      toast.success(t("protocols.observation.done"));
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : "Error");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen bg-background text-foreground" style={{ fontFamily: "var(--font-sans)" }}>
      <Header />
      <main className="max-w-2xl mx-auto px-6 py-10">
        <Link to="/protocols/$goal" params={{ goal }} className="text-xs uppercase tracking-widest text-muted-foreground hover:text-foreground">
          ← {t("protocols.back")}
        </Link>

        <h1 className="text-2xl md:text-3xl font-bold mt-3">{t("protocols.capture.title")}</h1>
        <p className="mt-1 text-muted-foreground text-sm">
          {def.title[locale]} · {step.title[locale]}
        </p>

        {/* Capture brief */}
        <div className="mt-6 rounded-2xl border border-foreground/10 bg-foreground/[0.02] p-4 space-y-3 text-sm">
          <div>
            <p className="text-[11px] uppercase tracking-widest text-muted-foreground">{t("protocols.capture.body_area")}</p>
            <p className="font-semibold mt-0.5">{bodyAreaName(bodyArea, locale)}</p>
          </div>
          {step.referenceHint && (
            <p className="text-xs text-foreground/80">💡 {step.referenceHint[locale]}</p>
          )}
          <div>
            <p className="text-[11px] uppercase tracking-widest text-muted-foreground mb-1">{t("protocols.capture.checklist")}</p>
            <ul className="text-xs text-muted-foreground list-disc list-inside space-y-0.5">
              <li>{t("protocols.capture.cond.light")}</li>
              <li>{t("protocols.capture.cond.bg")}</li>
              <li>{t("protocols.capture.cond.distance")}</li>
              <li>{t("protocols.capture.cond.framing")}</li>
            </ul>
          </div>
        </div>

        <PhotoGuide defaultOpen={!preview} goal={goal} />

        {viewsExpected > 1 && (
          <section className="mt-4 rounded-2xl border border-accent/30 bg-accent/5 px-4 py-3">
            <p className="text-[11px] uppercase tracking-widest text-muted-foreground">
              {t("protocols.capture.views.kicker")}
            </p>
            <p className="text-sm font-semibold mt-1">
              {t("protocols.capture.views.progress")
                .replace("{n}", String(totalCaptured))
                .replace("{total}", String(viewsExpected))}
            </p>
            {viewLabels.length > 0 && (
              <ol className="mt-2 grid grid-cols-1 sm:grid-cols-3 gap-1.5 text-xs">
                {viewLabels.slice(0, viewsExpected).map((lbl, i) => {
                  const done = i < totalCaptured;
                  return (
                    <li
                      key={i}
                      className={`flex items-center gap-1.5 rounded-md px-2 py-1 border ${done ? "border-accent/40 bg-accent/10 text-foreground" : "border-foreground/10 text-muted-foreground"}`}
                    >
                      <span className="inline-block size-4 rounded-full border grid place-items-center text-[10px]">
                        {done ? "✓" : i + 1}
                      </span>
                      <span className="truncate">{lbl}</span>
                    </li>
                  );
                })}
              </ol>
            )}
            {nextViewLabel && pendingDataUrl && (
              <div className="mt-3">
                <label className={`inline-flex items-center justify-center text-xs font-semibold rounded-full px-3 py-2 bg-foreground text-background cursor-pointer ${busy ? "opacity-40 pointer-events-none" : ""}`}>
                  + {t("protocols.capture.views.add").replace("{label}", nextViewLabel)}
                  <input
                    type="file" accept="image/*"
                    capture={bodyArea === "face" || bodyArea === "scalp" ? "user" : "environment"}
                    className="hidden"
                    disabled={!authed || busy}
                    onChange={(e) => e.target.files?.[0] && onFile(e.target.files[0])}
                  />
                </label>
              </div>
            )}
            {needsMoreViews && (
              <label className="mt-2 flex items-start gap-2 text-[11px] text-muted-foreground cursor-pointer">
                <input
                  type="checkbox"
                  checked={skipRemaining}
                  onChange={(e) => setSkipRemaining(e.target.checked)}
                  className="mt-0.5"
                />
                <span>{t("protocols.capture.views.skip.notice")}</span>
              </label>
            )}
            {extraViews.length > 0 && (
              <div className="mt-3 flex gap-1.5 flex-wrap">
                {[pendingDataUrl!, ...extraViews].map((src, i) => (
                  <div key={i} className="relative">
                    <img src={src} alt={`view ${i + 1}`} className="size-14 rounded-md object-cover border border-foreground/15" />
                    <span className="absolute -top-1 -right-1 bg-background border border-foreground/20 rounded-full text-[10px] px-1 leading-tight">
                      {i + 1}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </section>
        )}

        {/* Upload — empty state, or Capture Coach once an image is selected */}
        {!preview ? (
          <div className="mt-6 max-w-md mx-auto">
            <div className="aspect-[4/5] rounded-2xl border-2 border-dashed border-foreground/15 grid place-items-center text-center p-6">
              <div>
                <div className="size-12 rounded-full bg-accent/10 grid place-items-center mx-auto mb-3">
                  <div className="size-5 rounded-full border-2 border-accent" />
                </div>
                <p className="font-semibold text-sm">{step.title[locale]}</p>
                <p className="text-[11px] text-muted-foreground mt-1">{bodyAreaName(bodyArea, locale)}</p>
              </div>
            </div>
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2">
              <label className={`text-center text-sm font-semibold rounded-full px-4 py-3 bg-foreground text-background cursor-pointer ${(!authed || busy) ? "opacity-40 pointer-events-none" : ""}`}>
                {t("protocols.capture.take_selfie")}
                <input
                  type="file" accept="image/*" capture={bodyArea === "face" || bodyArea === "scalp" ? "user" : "environment"} className="hidden"
                  disabled={!authed || busy}
                  onChange={(e) => e.target.files?.[0] && onFile(e.target.files[0])}
                />
              </label>
              <label className={`text-center text-sm font-semibold rounded-full px-4 py-3 border border-foreground/20 hover:bg-foreground/5 cursor-pointer ${(!authed || busy) ? "opacity-40 pointer-events-none" : ""}`}>
                {t("protocols.capture.upload_short")}
                <input
                  type="file" accept="image/*" className="hidden"
                  disabled={!authed || busy}
                  onChange={(e) => e.target.files?.[0] && onFile(e.target.files[0])}
                />
              </label>
            </div>
          </div>
        ) : (
          <>
            <CaptureCoach
              dataUrl={preview}
              bodyArea={bodyArea}
              previousUrl={previousUrl}
              onQualityChange={setCaptureQuality}
            />
            {isPro && (
              <CaptureProPanel
                currentDataUrl={preview}
                bodyArea={bodyArea}
                locale={locale}
                onFramePicked={(url) => { setPendingDataUrl(url); setPreview(url); }}
              />
            )}
            <div className="mt-2 text-center">
              <label className="text-[11px] uppercase tracking-widest text-muted-foreground hover:text-foreground underline cursor-pointer">
                {t("coach.retake")}
                <input
                  type="file" accept="image/*" className="hidden"
                  disabled={!authed || busy}
                  onChange={(e) => e.target.files?.[0] && onRetake(e.target.files[0])}
                />
              </label>
            </div>
          </>
        )}

        {/* Reference + observations */}
        <div className="mt-6 space-y-4 text-sm">
          <div>
            <label className="text-[11px] uppercase tracking-widest text-muted-foreground">{t("protocols.capture.reference")}</label>
            <input
              type="text"
              value={referenceObject}
              onChange={(e) => setReferenceObject(e.target.value.slice(0, 64))}
              placeholder={t("protocols.capture.reference.placeholder")}
              className="mt-1 w-full rounded-lg border border-foreground/15 bg-background px-3 py-2 text-sm"
            />
          </div>
          <SliderRow label={t("protocols.capture.pain")} value={pain} min={0} max={10} onChange={setPain} />
          <SliderRow label={t("protocols.capture.redness")} value={redness} min={0} max={5} onChange={setRedness} />
          <SliderRow label={t("protocols.capture.itch")} value={itch} min={0} max={5} onChange={setItch} />
          <div>
            <label className="text-[11px] uppercase tracking-widest text-muted-foreground">{t("protocols.capture.notes")}</label>
            <textarea
              value={notes} onChange={(e) => setNotes(e.target.value.slice(0, 1000))}
              placeholder={t("protocols.capture.notes.placeholder")}
              rows={3}
              className="mt-1 w-full rounded-lg border border-foreground/15 bg-background px-3 py-2 text-sm"
            />
          </div>
        </div>

        <button
          disabled={!pendingDataUrl || !sessionId || busy}
          onClick={onSubmit}
          className="mt-6 w-full bg-foreground text-background rounded-full py-3 text-sm font-semibold disabled:opacity-40"
        >
          {busy ? t("protocols.capture.analyzing") : t("protocols.capture.submit")}
        </button>

        <p className="mt-3 text-[11px] text-muted-foreground text-center italic">
          {t("protocols.capture.disclaimer")}
        </p>

        {result && <ObservationCard data={result} t={t} />}
      </main>
    </div>
  );
}

function SliderRow({ label, value, min, max, onChange }: { label: string; value: number; min: number; max: number; onChange: (v: number) => void }) {
  return (
    <div>
      <div className="flex justify-between text-[11px] uppercase tracking-widest text-muted-foreground">
        <span>{label}</span>
        <span className="font-bold tabular-nums text-foreground">{value}</span>
      </div>
      <input
        type="range" min={min} max={max} step={1} value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="mt-1 w-full accent-accent"
      />
    </div>
  );
}

function ObservationCard({ data, t }: { data: Observation; t: (k: string) => string }) {
  return (
    <section className="mt-8 rounded-2xl border border-accent/30 bg-accent/5 p-5 text-sm space-y-4">
      {data.summary && (
        <div>
          <p className="text-[11px] uppercase tracking-widest text-muted-foreground">{t("protocols.observation.summary")}</p>
          <p className="mt-1">{data.summary}</p>
        </div>
      )}
      {data.changesVsPrevious && (
        <div>
          <p className="text-[11px] uppercase tracking-widest text-muted-foreground">{t("protocols.observation.changes")}</p>
          <p className="mt-1">{data.changesVsPrevious}</p>
        </div>
      )}
      {data.visibleFeatures && data.visibleFeatures.length > 0 && (
        <div>
          <p className="text-[11px] uppercase tracking-widest text-muted-foreground">{t("protocols.observation.visible")}</p>
          <ul className="mt-1 list-disc list-inside space-y-0.5">
            {data.visibleFeatures.map((f, i) => (
              <li key={i}>
                <strong>{f.label}</strong>
                {f.intensity ? ` · ${f.intensity}` : ""}
                {f.note ? ` — ${f.note}` : ""}
              </li>
            ))}
          </ul>
        </div>
      )}
      {data.requiresPractitionerReview && (
        <div className="rounded-lg border border-destructive/40 bg-destructive/10 p-3">
          <p className="font-bold text-destructive">{t("protocols.observation.referral")}</p>
          {data.practitionerReviewReason && <p className="text-xs mt-1">{data.practitionerReviewReason}</p>}
        </div>
      )}
      {data.redFlags && data.redFlags.length > 0 && (
        <div>
          <p className="text-[11px] uppercase tracking-widest text-muted-foreground">{t("protocols.observation.redflags")}</p>
          <ul className="mt-1 list-disc list-inside space-y-0.5">{data.redFlags.map((r, i) => <li key={i}>{r}</li>)}</ul>
        </div>
      )}
      {data.nextActions && data.nextActions.length > 0 && (
        <div>
          <p className="text-[11px] uppercase tracking-widest text-muted-foreground">{t("protocols.observation.next")}</p>
          <ul className="mt-1 list-disc list-inside space-y-0.5">{data.nextActions.map((r, i) => <li key={i}>{r}</li>)}</ul>
        </div>
      )}
      {data.imageQuality && (
        <p className="text-[11px] text-muted-foreground">
          {t("protocols.observation.quality")}: {data.imageQuality.lighting ?? "–"} · {data.imageQuality.focus ?? "–"} · {data.imageQuality.framing ?? "–"}
        </p>
      )}
      {data.disclaimer && <p className="text-[11px] italic text-muted-foreground">{data.disclaimer}</p>}
    </section>
  );
}

function bodyAreaName(area: string, locale: "fr" | "en" | "nl"): string {
  const labels: Record<"fr"|"en"|"nl", Record<string, string>> = {
    fr: { face: "Visage", scalp: "Cuir chevelu", eyebrows_lashes: "Sourcils / cils", neck_decollete: "Cou / décolleté", scar_site: "Site cicatriciel", hands: "Mains", abdomen: "Abdomen", limb: "Membre", full_body: "Corps entier", custom: "Zone personnalisée" },
    en: { face: "Face", scalp: "Scalp", eyebrows_lashes: "Brows / lashes", neck_decollete: "Neck / décolletage", scar_site: "Scar site", hands: "Hands", abdomen: "Abdomen", limb: "Limb", full_body: "Full body", custom: "Custom area" },
    nl: { face: "Gezicht", scalp: "Hoofdhuid", eyebrows_lashes: "Wenkbrauwen / wimpers", neck_decollete: "Hals / décolleté", scar_site: "Littekenplek", hands: "Handen", abdomen: "Buik", limb: "Ledemaat", full_body: "Volledig lichaam", custom: "Aangepaste zone" },
  };
  return labels[locale][area] ?? area;
}